import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { getCurrentUser, fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { ROLES, ROLE_CONFIG, getDashboardPath, hasPermission, getTenantType, getRoleConfig } from '../config/roleConfig';
import { demoUsers } from '../data/mockData';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function getInitialUser() {
  try {
    const saved = localStorage.getItem('forwardsflow_user');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed && parsed.email && parsed.role) {
        // Enrich stale localStorage entries with derived fields
        if (!parsed.tenantType) {
          parsed.tenantType = getTenantType(parsed.role) || 'bank';
        }
        if (!parsed.roleConfig) {
          parsed.roleConfig = getRoleConfig(parsed.role);
        }
        return parsed;
      }
      localStorage.removeItem('forwardsflow_user');
    }
  } catch (e) {
    console.error('Error reading saved user:', e);
    localStorage.removeItem('forwardsflow_user');
  }
  return null;
}

function getInitialTenant(user) {
  if (user && user.tenantId) {
    return {
      id: user.tenantId,
      name: user.tenantName,
      type: user.tenantType,
    };
  }
  return null;
}

export const AuthProvider = ({ children }) => {
  const initialUser = getInitialUser();
  const [user, setUser] = useState(initialUser);
  const [tenant, setTenant] = useState(getInitialTenant(initialUser));
  const [error, setError] = useState(null);
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);

  const loading = false;
  const loginInProgress = useRef(false);

  const loginWithCognito = useCallback(async () => {
    try {
      const cognitoUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        throw new Error('No Cognito ID token available');
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://api.forwardsflow.com/api/v1'}/auth/cognito-login`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Cognito login failed');
      }

      const data = await response.json();

      localStorage.setItem('authToken', data.token);
      if (data.refreshToken) {
        localStorage.setItem('refreshToken', data.refreshToken);
      }

      const authenticatedUser = {
        ...data.user,
        lastLogin: new Date().toISOString(),
        roleConfig: ROLE_CONFIG[data.user.role],
      };

      const tenantData = {
        id: data.user.tenantId,
        name: data.user.tenantName,
        type: data.user.tenantType,
      };

      localStorage.setItem('forwardsflow_user', JSON.stringify(authenticatedUser));
      setUser(authenticatedUser);
      setTenant(tenantData);
      setError(null);

      return { success: true, user: authenticatedUser, tenant: tenantData };
    } catch (err) {
      console.error('Cognito login error:', err);
      return { success: false, error: err.message };
    }
  }, []);

  // Listen for OAuth redirect completion
  useEffect(() => {
    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        await loginWithCognito();
      }
      if (payload.event === 'signInWithRedirect_failure') {
        console.error('OAuth redirect failed:', payload.data);
        setError('Social sign-in failed. Please try again.');
      }
    });

    return unsubscribe;
  }, [loginWithCognito]);

  const login = useCallback(async (email, password) => {
    if (loginInProgress.current) {
      console.log('Login already in progress, ignoring duplicate request');
      return { success: false, error: 'Login in progress' };
    }

    loginInProgress.current = true;
    setError(null);

    try {
      const cleanEmail = (email || '').trim().toLowerCase();
      const cleanPassword = (password || '').trim();

      console.log('Login attempt for:', cleanEmail);

      // 1. Check Demo Accounts First for Instant & Reliable Platform Access
      const foundDemoUser = Object.values(demoUsers).find(
        u => u.email.toLowerCase() === cleanEmail
      );

      if (foundDemoUser) {
        if (foundDemoUser.password !== cleanPassword) {
          throw new Error('Invalid email or password.');
        }

        const roleKey = foundDemoUser.role;
        const roleConfig = ROLE_CONFIG[roleKey] || ROLE_CONFIG[ROLES.FORWARDSFLOW_ADMIN];

        const authenticatedUser = {
          id: foundDemoUser.id,
          email: foundDemoUser.email,
          name: foundDemoUser.name,
          role: roleKey,
          tenantId: foundDemoUser.tenantId || 'demo-001',
          tenantName: foundDemoUser.tenantName || 'Demo Organization',
          tenantType: foundDemoUser.tenantType || getTenantType(roleKey) || 'bank',
          jobRole: foundDemoUser.jobRole || 'Administrator',
          lastLogin: new Date().toISOString(),
          roleConfig: roleConfig,
        };

        const tenantData = {
          id: authenticatedUser.tenantId,
          name: authenticatedUser.tenantName,
          type: authenticatedUser.tenantType,
        };

        const fakeToken = `demo_token_${foundDemoUser.id}_${Date.now()}`;
        localStorage.setItem('authToken', fakeToken);
        localStorage.setItem('forwardsflow_user', JSON.stringify(authenticatedUser));

        setUser(authenticatedUser);
        setTenant(tenantData);
        setError(null);
        loginInProgress.current = false;
        console.log('Demo Login Authorized:', authenticatedUser.email, 'Role:', authenticatedUser.role);
        return { success: true, user: authenticatedUser, tenant: tenantData };
      }

      // 2. Fallback to Live Backend API for non-demo accounts
      let data = null;
      let responseOk = false;

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://api.forwardsflow.com/api/v1'}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: cleanEmail, password: cleanPassword })
        });
        if (response.ok) {
          data = await response.json();
          responseOk = true;
        }
      } catch (apiErr) {
        console.warn('API connection offline:', apiErr);
      }

      if (responseOk && data) {
        // Check if MFA is required
        if (data.mfaRequired) {
          loginInProgress.current = false;
          return {
            success: false,
            mfaRequired: true,
            mfaToken: data.mfaToken,
            methods: data.methods,
          };
        }

        if (data.user) {
          localStorage.setItem('authToken', data.token);
          if (data.refreshToken) {
            localStorage.setItem('refreshToken', data.refreshToken);
          }

          const authenticatedUser = {
            ...data.user,
            lastLogin: new Date().toISOString(),
            roleConfig: ROLE_CONFIG[data.user.role],
          };

          const tenantData = {
            id: data.user.tenantId,
            name: data.user.tenantName,
            type: data.user.tenantType,
          };

          localStorage.setItem('forwardsflow_user', JSON.stringify(authenticatedUser));
          setUser(authenticatedUser);
          setTenant(tenantData);
          setError(null);

          // Check if MFA setup is recommended
          if (data.mfaSetupRequired) {
            setMfaSetupRequired(true);
          }

          loginInProgress.current = false;
          return { success: true, user: authenticatedUser, tenant: tenantData, mfaSetupRequired: data.mfaSetupRequired };
        }
      }

      throw new Error('Invalid email or password. Please verify your credentials or click a Quick Fill Demo button.');
    } catch (err) {
      console.error('Login error:', err);
      const errorMsg = err.message || 'Login failed. Please check your credentials and try again.';
      setError(errorMsg);
      loginInProgress.current = false;
      return { success: false, error: errorMsg };
    }
  }, []);

  // Complete login after MFA verification — receives { token, refreshToken, user }
  const completeLogin = useCallback((data) => {
    localStorage.setItem('authToken', data.token);
    if (data.refreshToken) {
      localStorage.setItem('refreshToken', data.refreshToken);
    }

    const authenticatedUser = {
      ...data.user,
      lastLogin: new Date().toISOString(),
      roleConfig: ROLE_CONFIG[data.user.role],
    };

    const tenantData = {
      id: data.user.tenantId,
      name: data.user.tenantName,
      type: data.user.tenantType,
    };

    localStorage.setItem('forwardsflow_user', JSON.stringify(authenticatedUser));
    setUser(authenticatedUser);
    setTenant(tenantData);
    setError(null);
  }, []);

  const clearMfaSetupRequired = useCallback(() => {
    setMfaSetupRequired(false);
  }, []);

  const logout = useCallback(async () => {
    localStorage.removeItem('forwardsflow_user');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setTenant(null);
    setError(null);
    loginInProgress.current = false;

    try {
      await signOut();
    } catch (err) {
      // Cognito signOut may fail if user was not signed in via Cognito — ignore
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value = {
    user,
    tenant,
    error,
    loading,
    isAuthenticated: !!user,
    mfaSetupRequired,
    login,
    loginWithCognito,
    completeLogin,
    clearMfaSetupRequired,
    logout,
    clearError,
    authToken: localStorage.getItem('authToken'),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
